/**
 * Hook for V2 reading journey stats.
 * Uses the same data-fetching pattern as useV2MultipleBookProgress
 * (useStableAvatar + useAuth) so progress appears instantly.
 * Subscribes to realtime changes on v2_reading_progress so the
 * ForYou page updates without needing a tab switch.
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { useStableAvatar } from '@/hooks/use-stable-avatar';
import { useProfileHierarchy } from '@/hooks/use-profile-hierarchy';

export interface V2ProfileJourney {
  profileId: string;
  profileName: string;
  avatarUrl: string | null;
  profileType: 'primary' | 'standard' | 'kids';
  isPrimary: boolean;
  booksStarted: number;
  booksCompleted: number;
  totalPagesRead: number;
  currentStreak: number;
  lastReadAt: string | null;
  favoriteBookId: string | null;
  progressEntries: {
    builderBookId: string;
    currentPage: number;
    totalPages: number;
    lastReadAt: string;
    startedAt: string;
    percentComplete: number;
  }[];
}

export function useV2ReadingJourney() {
  const { session } = useAuth();
  const { currentProfile } = useStableAvatar();
  const { profiles, activeProfile } = useProfileHierarchy();
  const queryClient = useQueryClient();
  const userId = session?.user?.id;
  const profileId = currentProfile?.id;

  // Realtime subscription: invalidate cache when progress changes
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel('v2-journey-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'v2_reading_progress',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['v2-reading-journey', userId] });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [userId, queryClient]);

  const { data, isLoading } = useQuery({
    queryKey: ['v2-reading-journey', userId, profileId],
    queryFn: async (): Promise<V2ProfileJourney[]> => {
      if (!userId || !profileId) return [];

      // Fetch ALL progress rows for this user (across all profiles)
      const { data: rows, error } = await supabase
        .from('v2_reading_progress')
        .select('builder_book_id, current_page, total_pages, last_read_at, started_at, profile_id')
        .eq('user_id', userId)
        .gt('current_page', 0)
        .order('last_read_at', { ascending: false });

      if (error) {
        console.warn('Error fetching V2 reading journey:', error.message);
        return [];
      }

      if (!rows?.length) return [];

      // Group progress by profile
      const byProfile = new Map<string, typeof rows>();
      rows.forEach(row => {
        const existing = byProfile.get(row.profile_id) || [];
        existing.push(row);
        byProfile.set(row.profile_id, existing);
      });

      // Build journeys from profiles we know about, or just the current profile
      const profileList = profiles.length > 0
        ? profiles
        : [{ id: profileId, name: currentProfile?.name || 'You', avatar_url: currentProfile?.avatar_url || null, profile_type: 'primary' as const, is_primary: true }];

      return profileList.map(profile => {
        const entries = byProfile.get(profile.id) || [];

        const progressEntries = entries.map(e => ({
          builderBookId: e.builder_book_id,
          currentPage: e.current_page,
          totalPages: e.total_pages,
          lastReadAt: e.last_read_at,
          startedAt: e.started_at,
          percentComplete: e.total_pages > 0
            ? (e.current_page >= e.total_pages - 1
              ? 100
              : Math.min(Math.round((e.current_page / e.total_pages) * 100), 100))
            : 0,
        }));

        const booksCompleted = progressEntries.filter(e => e.totalPages > 0 && e.currentPage >= e.totalPages - 1).length;
        const totalPagesRead = entries.reduce((sum, e) => sum + e.current_page, 0);

        let favoriteBookId: string | null = null;
        let maxPages = 0;
        entries.forEach(e => {
          if (e.current_page > maxPages) {
            maxPages = e.current_page;
            favoriteBookId = e.builder_book_id;
          }
        });

        const currentStreak = calculateStreak(entries.map(e => e.last_read_at));

        return {
          profileId: profile.id,
          profileName: profile.name,
          avatarUrl: profile.avatar_url,
          profileType: profile.profile_type,
          isPrimary: profile.is_primary,
          booksStarted: entries.length,
          booksCompleted,
          totalPagesRead,
          currentStreak,
          lastReadAt: entries[0]?.last_read_at || null,
          favoriteBookId,
          progressEntries,
        };
      });
    },
    enabled: !!userId && !!profileId,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  const journeys = data || [];
  const activeJourney = journeys.find(j => j.profileId === (activeProfile?.id || profileId)) || null;
  const childJourneys = journeys.filter(j => j.profileType === 'kids');
  const isPrimary = activeProfile?.is_primary ?? currentProfile?.is_primary ?? false;

  return {
    journeys,
    activeJourney,
    childJourneys,
    isPrimary,
    isLoading,
  };
}

function calculateStreak(dates: string[]): number {
  if (!dates.length) return 0;

  const uniqueDays = [...new Set(dates.map(d => {
    const dt = new Date(d);
    dt.setHours(0, 0, 0, 0);
    return dt.getTime();
  }))].sort((a, b) => b - a);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();
  const DAY = 86400000;

  let streak = 0;
  let expected = todayMs;

  // Allow starting from today or yesterday
  if (uniqueDays[0] !== todayMs) {
    expected = todayMs - DAY;
    if (uniqueDays[0] !== expected) return 0;
  }

  for (const dayMs of uniqueDays) {
    if (dayMs === expected) {
      streak++;
      expected -= DAY;
    } else if (dayMs < expected) {
      break;
    }
  }

  return streak;
}
